import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

/**
 * Decodes the `exp` claim (seconds since epoch) out of a JWT *without
 * verifying* the signature. The server is the source of truth for
 * validity — we only need `exp` to schedule a friendly client-side
 * logout before the next API call would 401.
 *
 * Returns the expiry as a Date, or `null` if the token is missing /
 * malformed / has no `exp` claim.
 */
function getTokenExpiry(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    // base64url → base64
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    if (typeof payload.exp !== 'number') return null;
    return new Date(payload.exp * 1000);
  } catch {
    return null;
  }
}

/**
 * Schedules a friendly auto-logout when the user's JWT is about to expire.
 *
 *   - `token`   the bearer JWT — read its `exp` claim to know when to fire.
 *   - `enabled` skip the timer when the user is not authenticated.
 *   - `onExpire`  called ~5s BEFORE the token's hard expiry. We fire early
 *               so the friendly logout flow runs while the token is still
 *               valid (the logout API call needs auth) rather than racing
 *               the server's 401.
 *   - `message`  toast string shown when the timer fires.
 *
 * The server enforces the actual expiry — this hook is purely about UX so
 * the user sees a clear "session ended" message instead of silently watching
 * the next API call fail.
 */
export function useSessionExpiryWatcher({ token, enabled, onExpire, message }) {
  const timerRef = useRef(null);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (!enabled || !token) return undefined;

    const expiry = getTokenExpiry(token);
    if (!expiry) return undefined;

    // Fire 5 seconds before the hard expiry so the logout request still
    // succeeds with a valid token. Cap setTimeout at ~24 days (2^31 ms)
    // since browsers truncate longer timers to 1ms and would otherwise
    // fire immediately — unlikely with an 8h TTL, but defensive.
    const msUntilExpiry = expiry.getTime() - Date.now() - 5000;
    if (msUntilExpiry <= 0) {
      onExpire();
      return undefined;
    }
    const ms = Math.min(msUntilExpiry, 2_000_000_000);

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (message) toast(message, { duration: 6000 });
      onExpire();
    }, ms);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [token, enabled, onExpire, message]);
}

// Exported for tests.
export { getTokenExpiry };
